import React, {
  useEffect,
  useRef,
  useMemo,
  useState,
  useCallback,
} from "react";
import { Flex, Box } from "rebass/styled-components";
import styled, { css } from "styled-components";
import { useSetRecoilState } from "recoil";
import { HiChevronLeft, HiChevronRight, HiX } from "react-icons/hi";
import { MdDesktopWindows, MdPhoneAndroid } from "react-icons/md";
import { Body } from "MVVM/Components/Text";
import { Subheader } from "MVVM/Components/Text";
import { GameLogos } from "MVVM/Components/Icons";
import { useUser } from "MVVM/Hooks/useUser";
import { analytics } from "MVVM/Analytics";
import { etaFromIso } from "MVVM/Utilities/approxTimeDisplay";
import { ESupportedGames, ModalType } from "MVVM/@types";
import { ClipFullModalState } from "MVVM/State/modals";
import type { ClipStatusTimeline, TimelineEntry } from "../../@types";
import {
  OverallState,
  MontageProgressState,
  HAPPY_PATH_EVENTS,
} from "../../@types";
import { useMontageProgress } from "../../Hooks/useMontageProgress";
import { Stepper } from "./Stepper";

type TrackerCardProps = {
  timelines: ClipStatusTimeline[];
  montageId?: string;
  onDismiss?: () => void;
};

const GAME_TO_SUPPORT_TAG: Record<number | string, string> = {
  7302: "cs2",
  730: "csgo",
  570: "dota2",
  704: "fortnite",
  101: "league_of_legends",
  NONE: "none",
};

const STATE_COPY: Record<string, string> = {
  REQUESTED: "Starting your clip",
  CREATING: "Building your clip",
  SUBMITTED: "In the queue",
  QUEUED: "Almost there — waiting to record",
  CAPTURING: "Recording your gameplay",
  COMPLETED: "Your clip is ready!",
  COMPLETED_DESKTOP: "Your clip is ready!",
  COMPLETED_MOBILE: "Mobile ready — finishing desktop",
};

const ERROR_COPY: Record<string, string> = {
  ERR_VALIDATION: "Something didn't look right — validation failed",
  ERR_CREATION: "Hit a snag creating your clip",
  ERR_CAPTURE: "Couldn't finish recording",
  ERR_TIMEOUT: "This one took too long and timed out",
};

const isRetrying = (events: TimelineEntry[]): boolean => {
  let submittedCount = 0;
  for (const entry of events) {
    if (entry.event === "SUBMITTED" && !entry.isMobile) submittedCount++;
  }
  return submittedCount >= 2;
};

const resolveActiveIndex = (events: TimelineEntry[]): number => {
  const reached = new Set(
    events.filter((e) => !e.isMobile).map((e) => e.event),
  );
  let highest = -1;
  for (let i = 0; i < HAPPY_PATH_EVENTS.length; i++) {
    if (reached.has(HAPPY_PATH_EVENTS[i])) highest = i;
  }
  return highest;
};

const MAX_STEP_IDX = HAPPY_PATH_EVENTS.length - 1;

const MIN_STEP_S = 6;
const FALLBACK_STEP_S = 15;

const getSegmentDuration = (segIdx: number, eta?: string): number => {
  if (segIdx < 3) return MIN_STEP_S;
  if (eta) {
    const msLeft = Math.max(0, new Date(eta).getTime() - Date.now());
    const segsLeft = 5 - segIdx;
    return Math.max(MIN_STEP_S, msLeft / 1000 / segsLeft);
  }
  return FALLBACK_STEP_S;
};

const STALE_THRESHOLD_MS = 10 * 60 * 1000;

const getClipProgress = (timeline: ClipStatusTimeline): number => {
  const idx = resolveActiveIndex(timeline.events);
  if (
    timeline.overallState === OverallState.COMPLETED ||
    timeline.overallState === OverallState.COMPLETED_DESKTOP
  ) {
    return 1;
  }
  if (idx < 0) return 0;
  return idx / MAX_STEP_IDX;
};

export const TrackerCard = ({
  timelines,
  montageId,
  onDismiss,
}: TrackerCardProps) => {
  const { allstarUser } = useUser();
  const setClipModal = useSetRecoilState(ClipFullModalState);
  const [pageIndex, setPageIndex] = useState(0);

  const { montage } = useMontageProgress(montageId);

  const isMontage = !!montageId;
  const clips = isMontage && montage?.clips?.length ? montage.clips : timelines;
  const safeIndex = Math.max(0, Math.min(pageIndex, clips.length - 1));
  const timeline = clips[safeIndex];
  const showPagination = !isMontage && clips.length > 1;

  const prev = useCallback(() => setPageIndex((i) => Math.max(0, i - 1)), []);
  const next = useCallback(
    () => setPageIndex((i) => Math.min(clips.length - 1, i + 1)),
    [clips.length],
  );

  const activeIdx = resolveActiveIndex(timeline.events);
  const activeEvent = HAPPY_PATH_EVENTS[activeIdx];
  const isError = timeline.overallState === OverallState.ERRORED;
  const isComplete =
    timeline.overallState === OverallState.COMPLETED ||
    timeline.overallState === OverallState.COMPLETED_DESKTOP;
  const retry = useMemo(() => isRetrying(timeline.events), [timeline.events]);

  const fillDuration = getSegmentDuration(
    activeIdx,
    timeline.desktop?.estimatedCompletionAt,
  );

  const shownRef = useRef(false);
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    analytics.track("Clip Status Card Shown", {
      trackId: timeline.trackId,
      game: timeline.game,
      contentType: timeline.contentType,
      overallState: timeline.overallState,
      queueName: timeline.queueName,
      montageId,
      clipCount: clips.length,
    });
  }, [timeline, montageId, clips.length]);

  const isStale =
    !isError &&
    !isComplete &&
    timeline.lastEventAt &&
    Date.now() - new Date(timeline.lastEventAt).getTime() > STALE_THRESHOLD_MS;

  const eta = timeline.desktop?.estimatedCompletionAt
    ? etaFromIso(timeline.desktop.estimatedCompletionAt)
    : null;

  const clipTitleMap = useRef(new Map<string, string>());
  if (timeline.clip?.title)
    clipTitleMap.current.set(timeline.trackId, timeline.clip.title);
  const clipTitle = clipTitleMap.current.get(timeline.trackId);

  const errorCode = timeline.desktop?.errorCode ?? timeline.mobile?.errorCode;

  const buildSupportLink = () => {
    const profileLink = `https://allstar.gg/profile?user=${allstarUser?.user?._id}`;
    const gameTag = GAME_TO_SUPPORT_TAG[timeline.game ?? "NONE"] ?? "none";
    return `https://help.allstar.gg/hc/en-us/requests/new?ticket_form_id=12129622526487&tf_12391937440663=${timeline.trackId}&tf_12579506242199=${profileLink}&tf_12070892016151=${gameTag}&tf_12153827873687=errored_clip`;
  };

  const statusCopy = retry
    ? "Re-rendering — this sometimes happens, hang tight"
    : isStale
    ? "Taking longer than usual"
    : isError
    ? ERROR_COPY[errorCode ?? ""] ?? "Processing error"
    : STATE_COPY[activeEvent] ?? "Processing";

  const terminalVariant: "completed" | "errored" | "none" = isComplete
    ? "completed"
    : isError
    ? "errored"
    : "none";

  const isMontageTerminal =
    montage?.state === MontageProgressState.COMPLETED ||
    montage?.state === MontageProgressState.ERROR;
  const montageLabel = montageId
    ? montage && !isMontageTerminal
      ? `Montage — ${montage.completed} of ${montage.total} rendered`
      : montage?.state === MontageProgressState.ERROR
      ? "Montage error"
      : "Montage"
    : null;

  const isTerminal = isComplete || isError;
  const desktopReady = !!timeline.desktop?.completedAt;
  const mobileReady = !!timeline.mobile?.completedAt;
  const hasMobile = !!timeline.mobile;

  const openClip = useCallback(
    (orientation: "LANDSCAPE" | "PORTRAIT") => {
      if (!timeline.clipId) return;
      setClipModal({
        isOpen: ModalType.Clip,
        shareId: timeline.clipId,
        orientation,
      });
      analytics.track("Clip Status CTA Clicked", {
        trackId: timeline.trackId,
        game: timeline.game,
        contentType: timeline.contentType,
        action: "watch",
        orientation,
      });
    },
    [
      timeline.clipId,
      timeline.trackId,
      timeline.game,
      timeline.contentType,
      setClipModal,
    ],
  );

  const montageTerminalVariant: "completed" | "errored" | "none" = isMontage
    ? montage?.state === MontageProgressState.COMPLETED
      ? "completed"
      : montage?.state === MontageProgressState.ERROR
      ? "errored"
      : "none"
    : "none";

  const isMontageComplete = montage?.state === MontageProgressState.COMPLETED;

  if (isMontage) {
    return (
      <CardBox
        role="status"
        aria-live="polite"
        $terminal={montageTerminalVariant}
      >
        <TopRow>
          <MontageLabel>{montageLabel ?? "Montage"}</MontageLabel>
          {onDismiss && (
            <DismissButton onClick={onDismiss} aria-label="Dismiss">
              <HiX size={14} />
            </DismissButton>
          )}
        </TopRow>

        {isMontageComplete ? (
          <ReadyRows>
            <ReadyRow>
              <MdDesktopWindows size={14} />
              <ReadyText>Your montage is ready</ReadyText>
              <ViewLink
                as="a"
                href={`/montage?montage=${montageId}`}
                onClick={() => {
                  analytics.track("Clip Status CTA Clicked", {
                    montageId,
                    action: "watch",
                    contentType: "MONTAGE",
                  });
                  onDismiss?.();
                }}
              >
                View
              </ViewLink>
            </ReadyRow>
          </ReadyRows>
        ) : (
          <MontageClipList>
            {clips.map((clip, i) => {
              const progress = getClipProgress(clip);
              const clipDone =
                clip.overallState === OverallState.COMPLETED ||
                clip.overallState === OverallState.COMPLETED_DESKTOP;
              const clipErrored = clip.overallState === OverallState.ERRORED;

              return (
                <MontageClipRow key={clip.trackId}>
                  <MontageClipLabel $done={clipDone} $error={clipErrored}>
                    Clip {i + 1}
                  </MontageClipLabel>
                  <MiniBarTrack>
                    <MiniBarFill
                      $progress={progress}
                      $done={clipDone}
                      $error={clipErrored}
                    />
                  </MiniBarTrack>
                </MontageClipRow>
              );
            })}
          </MontageClipList>
        )}
      </CardBox>
    );
  }

  return (
    <CardBox role="status" aria-live="polite" $terminal={terminalVariant}>
      <HeaderRow>
        {timeline.game != null && (
          <>
            <GameLogos
              type={timeline.game as unknown as ESupportedGames}
              size={["16", "18"]}
              styles={{ marginRight: 0 }}
            />
            <MiniDivider />
          </>
        )}
        <ClipTitle>
          {clipTitle || (timeline.clipId ? "Clip" : "New clip")}
        </ClipTitle>
        <HeaderActions>
          {showPagination && (
            <PaginationNav>
              <NavButton onClick={prev} disabled={safeIndex === 0}>
                <HiChevronLeft size={14} />
              </NavButton>
              <PageIndicator>
                {safeIndex + 1}/{clips.length}
              </PageIndicator>
              <NavButton
                onClick={next}
                disabled={safeIndex === clips.length - 1}
              >
                <HiChevronRight size={14} />
              </NavButton>
            </PaginationNav>
          )}
          {eta && !isError && !isComplete && (
            <EtaText color={isStale ? "caution" : "envy"}>{eta}</EtaText>
          )}
          {onDismiss && (
            <DismissButton onClick={onDismiss} aria-label="Dismiss">
              <HiX size={14} />
            </DismissButton>
          )}
        </HeaderActions>
      </HeaderRow>

      {isComplete ? (
        <ReadyRows>
          <ReadyRow>
            <MdDesktopWindows size={14} />
            <ReadyText>
              {desktopReady
                ? "Your Desktop clip is ready"
                : "Desktop rendering"}
            </ReadyText>
            {desktopReady && (
              <ViewLink onClick={() => openClip("LANDSCAPE")}>View</ViewLink>
            )}
          </ReadyRow>
          {hasMobile && (
            <ReadyRow>
              <MdPhoneAndroid size={14} />
              <ReadyText>
                {mobileReady ? "Your Mobile clip is ready" : "Mobile rendering"}
              </ReadyText>
              {mobileReady && (
                <ViewLink onClick={() => openClip("PORTRAIT")}>View</ViewLink>
              )}
            </ReadyRow>
          )}
        </ReadyRows>
      ) : (
        <>
          <Stepper
            activeIdx={activeIdx}
            isError={isError}
            fillDuration={fillDuration}
          />

          <StatusRow>
            <StatusText
              color={isError ? "alert" : isStale ? "caution" : "chalk"}
            >
              {statusCopy}
            </StatusText>
          </StatusRow>

          {(isError || isStale) && (
            <CTARow>
              <SupportLink
                href={buildSupportLink()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  analytics.track("Clip Status CTA Clicked", {
                    trackId: timeline.trackId,
                    game: timeline.game,
                    contentType: timeline.contentType,
                    action: "get_help",
                  })
                }
              >
                Get help
              </SupportLink>
            </CTARow>
          )}
        </>
      )}
    </CardBox>
  );
};

const CardBox = styled(Box)<{
  $terminal: "completed" | "errored" | "none";
}>`
  background-color: ${({ $terminal, theme }) =>
    $terminal === "none" ? theme.colors.navy : theme.colors.navy};
  border-radius: 12px;
  border: 1px solid
    ${({ $terminal, theme }) =>
      $terminal === "completed"
        ? theme.colors.envyAlpha20
        : $terminal === "errored"
        ? theme.colors.alertAlpha20
        : theme.colors.chalkAlpha20};
  padding: 10px 12px;
  width: 300px;

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    width: 100%;
  }
`;

const TopRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const MontageLabel = styled(Body)`
  font-weight: 600;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.ice};
`;

const HeaderActions = styled(Flex)`
  align-items: center;
  gap: 6px;
  margin-left: auto;
  flex-shrink: 0;
`;

const PaginationNav = styled(Flex)`
  align-items: center;
  gap: 2px;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.chalk};
  padding: 0;
  cursor: pointer;
  opacity: 0.7;

  &:hover:not(:disabled) {
    opacity: 1;
  }

  &:disabled {
    opacity: 0.2;
    cursor: default;
  }
`;

const PageIndicator = styled(Body)`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  min-width: 28px;
  text-align: center;
`;

const HeaderRow = styled(Flex)`
  align-items: center;
  margin-bottom: 2px;
`;

const MiniDivider = styled.div`
  width: 1px;
  height: 16px;
  margin: 0 6px;
  background-color: ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 999px;
`;

const ClipTitle = styled(Body)`
  font-weight: 600;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EtaText = styled(Body)`
  font-weight: 600;
  font-size: 12px;
  margin-left: auto;
  flex-shrink: 0;
`;

const StatusRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
`;

const StatusText = styled(Subheader)`
  font-weight: 500;
  font-size: 12px;
`;

const ReadyRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`;

const ReadyRow = styled(Flex)`
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.envy};
`;

const ReadyText = styled(Subheader)`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.chalk};
`;

const ViewLink = styled.button`
  margin-left: auto;
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const CTARow = styled(Flex)`
  gap: 8px;
  margin-top: 6px;

  &:empty {
    display: none;
  }
`;

const SupportLink = styled.a`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.ice};
  text-decoration: underline;
  text-decoration-color: ${({ theme }) => theme.colors.envy};
  text-underline-offset: 2px;
  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const DismissButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: none;
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.chalk};
    background-color: ${({ theme }) => theme.colors.chalkAlpha20};
  }
`;

const MontageClipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 2px;
`;

const MontageClipRow = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const MontageClipLabel = styled(Subheader)<{
  $done: boolean;
  $error: boolean;
}>`
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
  width: 38px;
  color: ${({ $done, $error, theme }) =>
    $error
      ? theme.colors.alert
      : $done
      ? theme.colors.envy
      : theme.colors.chalkAlpha60};
`;

const MiniBarTrack = styled.div`
  flex: 1;
  height: 3px;
  background-color: ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 2px;
  overflow: hidden;
`;

const MiniBarFill = styled.div<{
  $progress: number;
  $done: boolean;
  $error: boolean;
}>`
  height: 100%;
  width: ${({ $progress }) => `${$progress * 100}%`};
  border-radius: 2px;
  background-color: ${({ $done, $error, theme }) =>
    $error
      ? theme.colors.alert
      : $done
      ? theme.colors.envy
      : theme.colors.envy};
  transition: width 0.6s ease-out;
  ${({ $done, $error }) =>
    !$done &&
    !$error &&
    css`
      opacity: 0.7;
    `}
`;
